import React, { useState, useRef, useEffect } from 'react';
import { X, Upload } from 'lucide-react';
import { useMediaUpload, getMediaUrl } from '../../../hooks/useMediaUpload';
import resolveAvatar from '../../../lib/resolveAvatar';

interface Community {
  id: string;
  name: string;
  isMember: boolean;
}

interface CommunityPostModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (postData: {
    title: string;
    content: string;
    tags: string[];
    mediaUrl: string;
    mediaPosition: 'above' | 'below';
    communityId?: string;
    postType?: 'community' | 'forum';
    visibility?: 'public' | 'members-only';
  }) => Promise<void>;
  currentUser: {
    id: string;
    username: string;
    displayName?: string;
    avatar?: string;
  } | null;
  communityName?: string;
  showPostTypeSelector?: boolean; // Whether to show community vs forum toggle
  communities?: Community[]; // List of communities for selection
  hideCommunitySelector?: boolean; // Hide the community selector dropdown
  cardPosition?: 'left' | 'center' | 'right'; // Position of the card for modal placement
  theme: {
    textColor: string;
    accentColor: string;
    cardBg: string;
  };
  scale: { w: number; h: number };
  scaleW: (val: number, scale: number) => number;
  scaleH: (val: number, scale: number) => number;
  getCardBackground: (theme: any, customization: any, fallback: string) => string;
  customization?: any;
}

export function CommunityPostModal({
  isOpen,
  onClose,
  onSubmit,
  currentUser,
  communityName,
  showPostTypeSelector = true,
  communities = [],
  hideCommunitySelector = false,
  cardPosition = 'center',
  theme,
  scale,
  scaleW,
  scaleH,
  getCardBackground,
  customization
}: CommunityPostModalProps) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaPosition, setMediaPosition] = useState<'above' | 'below'>('below');
  const [communityId, setCommunityId] = useState<string>('');
  const [postType, setPostType] = useState<'community' | 'forum'>('community');
  const [visibility, setVisibility] = useState<'public' | 'members-only'>('public');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadFile, isUploading, uploadProgress, fileId, error: uploadError } = useMediaUpload();

  // Update media URL when file upload completes
  useEffect(() => {
    if (fileId) {
      setMediaUrl(getMediaUrl(fileId));
    }
  }, [fileId]);

  const handleSubmit = async () => {
    if (!content.trim() || !communityId) return;

    setIsSubmitting(true);
    try {
      const tags = tagInput
        .split(/[,\s#]+/)
        .map(t => t.trim())
        .filter(t => t.length > 0);

      await onSubmit({
        title,
        content,
        tags,
        mediaUrl,
        mediaPosition,
        communityId,
        postType,
        visibility
      });

      // Reset form
      setTitle('');
      setContent('');
      setTagInput('');
      setMediaUrl('');
      setMediaPosition('below');
      setCommunityId('');
      setPostType('community');
      setVisibility('public');
      onClose();
    } catch (error) {
      console.error('Failed to create post:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        await uploadFile(file);
      } catch (error) {
        console.error('Upload failed:', error);
        alert('Failed to upload file. Please try again.');
      }
    }
    if (e.target) e.target.value = '';
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed rounded-xl shadow-2xl border"
      style={{
        top: cardPosition === 'left' ? '13.6rem' : '10rem',
        ...(cardPosition === 'left'
          ? { right: '1.5rem' }
          : { left: '1.5rem' }),
        backgroundColor: theme.cardBg,
        borderColor: theme.accentColor + '60',
        zIndex: 43,
        width: '22vw',
        maxWidth: '600px',
        minWidth: '350px',
        animation: 'slideDown 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
        <div style={{ padding: `${scaleH(20, scale.h)}px` }}>
          {/* Header */}
          <div className="flex items-center justify-between" style={{ marginBottom: `${scaleH(16, scale.h)}px` }}>
            <h3 className="font-bold" style={{ color: theme.textColor, fontSize: `${scaleW(18, scale.w)}px` }}>
              {communityName ? `Post to ${communityName}` : 'Create Community Post'}
            </h3>
            <button
              onClick={onClose}
              className="hover:opacity-70 transition-opacity"
              style={{ color: theme.textColor }}
            >
              <X size={20} />
            </button>
          </div>

          {/* Community Selector - Only show if not hidden and communities available */}
          {!hideCommunitySelector && communities.length > 0 && (
            <div className="mb-4">
              <select
                value={communityId}
                onChange={(e) => setCommunityId(e.target.value)}
                className="w-full p-3 rounded-lg focus:outline-none transition-all"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: theme.textColor,
                  border: `1px solid ${theme.accentColor}40`,
                  boxShadow: '0 0 0 0px transparent',
                }}
              >
                <option value="" style={{ backgroundColor: '#1e3a5f', color: theme.textColor }}>
                  Select a community...
                </option>
                {communities.map((community) => (
                  <option
                    key={community.id}
                    value={community.id}
                    style={{ backgroundColor: '#1e3a5f', color: theme.textColor }}
                  >
                    {community.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Post Type Selector */}
          {showPostTypeSelector && (
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setPostType('community')}
                className="flex-1 px-4 py-2 rounded-lg transition-all font-medium"
                style={{
                  backgroundColor: postType === 'community' ? theme.accentColor : 'rgba(255, 255, 255, 0.1)',
                  color: postType === 'community' ? '#ffffff' : theme.textColor,
                  border: `1px solid ${postType === 'community' ? theme.accentColor : theme.accentColor + '40'}`
                }}
              >
                Regular Post
              </button>
              <button
                onClick={() => setPostType('forum')}
                className="flex-1 px-4 py-2 rounded-lg transition-all font-medium"
                style={{
                  backgroundColor: postType === 'forum' ? theme.accentColor : 'rgba(255, 255, 255, 0.1)',
                  color: postType === 'forum' ? '#ffffff' : theme.textColor,
                  border: `1px solid ${postType === 'forum' ? theme.accentColor : theme.accentColor + '40'}`
                }}
              >
                Forum Post
              </button>
            </div>
          )}

          {/* User Info */}
          {currentUser && (
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: `linear-gradient(135deg, ${theme.accentColor}, ${theme.accentColor}dd)` }}
              >
                {currentUser.avatar ? (
                  <img src={resolveAvatar(currentUser.avatar) || currentUser.avatar} alt="You" className="w-full h-full rounded-full object-cover" />
                ) : (
                  <span style={{ color: theme.textColor }}>👤</span>
                )}
              </div>
              <div>
                <p className="font-semibold text-sm" style={{ color: theme.textColor }}>
                  {currentUser.displayName || currentUser.username}
                </p>
                <p className="text-xs opacity-70" style={{ color: theme.textColor }}>
                  @{currentUser.username}
                </p>
              </div>
            </div>
          )}

          {/* Title Input */}
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title (optional)..."
            className="w-full px-4 py-3 rounded-lg text-base font-bold mb-3 border focus:outline-none transition-all"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderColor: `${theme.accentColor}40`,
              color: theme.textColor
            }}
            maxLength={100}
          />

          {/* Content Textarea */}
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={postType === 'forum' ? 'Start a discussion...' : 'Share with the community...'}
            className="w-full rounded-lg p-3 text-sm resize-none border focus:outline-none transition-all"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderColor: `${theme.accentColor}40`,
              color: theme.textColor
            }}
            rows={10}
            maxLength={5000}
          />
          <div className="text-right text-xs mt-1 opacity-70" style={{ color: theme.textColor }}>
            {content.length}/5000
          </div>

          {/* Media Section */}
          <div className="mb-3">
            <input
              type="text"
              value={mediaUrl}
              onChange={(e) => setMediaUrl(e.target.value)}
              placeholder="Image URL (optional)"
              className="w-full p-3 rounded-lg mb-2 focus:outline-none transition-all"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: theme.textColor,
                border: `1px solid ${theme.accentColor}40`
              }}
            />
            <div className="flex gap-2">
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all disabled:opacity-50"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: theme.textColor,
                  border: `1px solid ${theme.accentColor}40`
                }}
              >
                <Upload size={16} />
                {isUploading ? `Uploading... ${uploadProgress}%` : 'Upload Media'}
              </button>
              {mediaUrl && (
                <button
                  onClick={() => setMediaUrl('')}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
                  style={{
                    backgroundColor: 'rgba(255, 0, 0, 0.2)',
                    color: theme.textColor,
                    border: '1px solid rgba(255, 0, 0, 0.4)'
                  }}
                >
                  <X size={16} />
                  Clear
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              className="hidden"
              onChange={handleFileChange}
            />

            {/* Media Position Selector */}
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => setMediaPosition('above')}
                className="flex-1 px-3 py-2 rounded-lg transition-all text-sm font-medium"
                style={{
                  backgroundColor: mediaPosition === 'above' ? theme.accentColor : 'rgba(255, 255, 255, 0.05)',
                  color: mediaPosition === 'above' ? '#ffffff' : theme.textColor,
                  border: `1px solid ${mediaPosition === 'above' ? theme.accentColor : theme.accentColor + '30'}`
                }}
              >
                Above Content
              </button>
              <button
                onClick={() => setMediaPosition('below')}
                className="flex-1 px-3 py-2 rounded-lg transition-all text-sm font-medium"
                style={{
                  backgroundColor: mediaPosition === 'below' ? theme.accentColor : 'rgba(255, 255, 255, 0.05)',
                  color: mediaPosition === 'below' ? '#ffffff' : theme.textColor,
                  border: `1px solid ${mediaPosition === 'below' ? theme.accentColor : theme.accentColor + '30'}`
                }}
              >
                Below Content
              </button>
            </div>
          </div>

          {/* Tags Input */}
          <input
            type="text"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            placeholder="Tags (e.g. #tag1 #tag2 or tag1, tag2)"
            className="w-full p-3 rounded-lg mb-4 focus:outline-none transition-all"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: theme.textColor,
              border: `1px solid ${theme.accentColor}40`
            }}
          />

          {/* Visibility Selector */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setVisibility('public')}
              className="flex-1 px-4 py-2 rounded-lg transition-all text-sm font-medium"
              style={{
                backgroundColor: visibility === 'public' ? theme.accentColor : 'rgba(255, 255, 255, 0.1)',
                color: visibility === 'public' ? '#ffffff' : theme.textColor,
                border: `1px solid ${visibility === 'public' ? theme.accentColor : theme.accentColor + '40'}`
              }}
            >
              Public
            </button>
            <button
              onClick={() => setVisibility('members-only')}
              className="flex-1 px-4 py-2 rounded-lg transition-all text-sm font-medium"
              style={{
                backgroundColor: visibility === 'members-only' ? theme.accentColor : 'rgba(255, 255, 255, 0.1)',
                color: visibility === 'members-only' ? '#ffffff' : theme.textColor,
                border: `1px solid ${visibility === 'members-only' ? theme.accentColor : theme.accentColor + '40'}`
              }}
            >
              Members Only
            </button>
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={!content.trim() || isSubmitting}
            className="w-full py-3 rounded-lg font-semibold transition-all disabled:opacity-50"
            style={{
              backgroundColor: theme.accentColor,
              color: theme.textColor
            }}
          >
            {isSubmitting ? 'Posting...' : `Post to Community`}
          </button>
        </div>
    </div>
  );
}
